<template>
	<div class="container mt-4">
		<div class="d-flex justify-content-between align-items-center mb-3">
			<h2>
				<RouterLink
					:to="`/_/review/mapvision/type/${typeName}`"
					class="btn btn-outline-secondary btn-sm me-2"
				>
					<i class="bi bi-arrow-left"></i>
				</RouterLink>
				{{ title }}
			</h2>
		</div>

		<div v-if="loading" class="text-center py-4">
			<div class="spinner-border text-primary" role="status">
				<span class="visually-hidden">Loading...</span>
			</div>
		</div>

		<div v-else-if="error" class="alert alert-danger">{{ error }}</div>

		<div v-else-if="data" class="card">
			<div class="card-body">
				<table class="table table-sm table-striped mb-0">
					<tbody>
						<tr v-for="[key, value] in entries" :key="key">
							<th class="text-muted font-monospace" style="width: 260px">
								{{ key }}
							</th>
							<td>
								<span
									v-if="value === null || value === undefined"
									class="text-muted"
									>—</span
								>
								<code v-else-if="typeof value === 'object'">{{
									JSON.stringify(value)
								}}</code>
								<span v-else class="text-break">{{ value }}</span>
							</td>
						</tr>
					</tbody>
				</table>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { apiClient } from "@/client";
import type { MapvisionReviewTypeRow } from "@/type/api";

const route = useRoute();

const loading = ref(false);
const error = ref("");
const data = ref<MapvisionReviewTypeRow | null>(null);

const typeName = computed(() => route.params.type as string);
const id = computed(() => route.params.id as string);

const title = computed(() => {
	const label = data.value?.label ?? typeName.value;
	return `${label} #${id.value}`;
});

const entries = computed(() =>
	data.value ? Object.entries(data.value.row) : [],
);

async function load() {
	loading.value = true;
	error.value = "";
	try {
		data.value = await apiClient.JSONGet<MapvisionReviewTypeRow>(
			`/api/mapvision/type/${encodeURIComponent(typeName.value)}/${encodeURIComponent(id.value)}`,
		);
	} catch (e: any) {
		if (e?.response?.status === 404) {
			error.value = "Record not found";
		} else {
			error.value =
				e?.response?.data?.message || e?.message || "Failed to load record";
		}
		data.value = null;
	} finally {
		loading.value = false;
	}
}

watch([typeName, id], () => {
	void load();
});

onMounted(() => {
	void load();
});
</script>
